/**
 * A cor de cada etapa do funil, e o semáforo de tempo parado.
 *
 * DUAS FAMÍLIAS DE COR, E MISTURAR AS DUAS É O ERRO QUE ESTE ARQUIVO EVITA.
 *
 *   IDENTIDADE → qual etapa é esta?     paleta `etapa-*` (azul, violeta, …)
 *   ESTADO     → como vai este cliente?  paleta semântica (ok, warn, crit)
 *
 * São perguntas diferentes e por isso são paletas diferentes. Se "Ganho"
 * fosse pintado de `ok`, a mesma cor significaria "número apto" no parque e
 * "negócio fechado" no funil — e a partir da segunda leitura ambígua ninguém
 * mais confia na cor, que era justamente o que a tornava útil.
 *
 * ⚠ AS CLASSES SÃO LITERAIS, NUNCA MONTADAS.
 * `bg-etapa-${tom}` não funciona: o Tailwind varre o código-fonte procurando
 * nomes completos e não gera a classe que só existe em tempo de execução. O
 * bug é silencioso — a cor simplesmente não aparece e nada acusa. É a mesma
 * armadilha que `ui/base.tsx` documenta no mapa de tons.
 */

export interface CorDeEtapa {
  /** Cor cheia: faixa da coluna, barra do funil, listra do cartão. */
  readonly faixa: string;
  /** Fundo suave: cabeçalho da coluna, selo, avatar. */
  readonly suave: string;
  /** Tinta legível sobre o suave. Nunca cor cheia sobre suave. */
  readonly tinta: string;
  /** Borda na cor cheia — usada no alvo de arrastar. */
  readonly borda: string;
  /** Nome em português, para o seletor de cor. */
  readonly nome: string;
}

/**
 * As oito da paleta de etapa vêm primeiro: são as que a tela oferece.
 * Os sete semânticos ficam no fim por compatibilidade — a migration 032
 * semeou com eles, e etapa que alguém já pintou não se repinta sozinha.
 */
export const CORES_DE_ETAPA: Record<string, CorDeEtapa> = {
  azul: {
    faixa: "bg-etapa-azul",
    suave: "bg-etapa-azul-suave",
    tinta: "text-etapa-azul-tinta",
    borda: "border-etapa-azul",
    nome: "Azul",
  },
  violeta: {
    faixa: "bg-etapa-violeta",
    suave: "bg-etapa-violeta-suave",
    tinta: "text-etapa-violeta-tinta",
    borda: "border-etapa-violeta",
    nome: "Violeta",
  },
  ciano: {
    faixa: "bg-etapa-ciano",
    suave: "bg-etapa-ciano-suave",
    tinta: "text-etapa-ciano-tinta",
    borda: "border-etapa-ciano",
    nome: "Ciano",
  },
  verde: {
    faixa: "bg-etapa-verde",
    suave: "bg-etapa-verde-suave",
    tinta: "text-etapa-verde-tinta",
    borda: "border-etapa-verde",
    nome: "Verde",
  },
  ambar: {
    faixa: "bg-etapa-ambar",
    suave: "bg-etapa-ambar-suave",
    tinta: "text-etapa-ambar-tinta",
    borda: "border-etapa-ambar",
    nome: "Âmbar",
  },
  rosa: {
    faixa: "bg-etapa-rosa",
    suave: "bg-etapa-rosa-suave",
    tinta: "text-etapa-rosa-tinta",
    borda: "border-etapa-rosa",
    nome: "Rosa",
  },
  vermelho: {
    faixa: "bg-etapa-vermelho",
    suave: "bg-etapa-vermelho-suave",
    tinta: "text-etapa-vermelho-tinta",
    borda: "border-etapa-vermelho",
    nome: "Vermelho",
  },
  cinza: {
    faixa: "bg-etapa-cinza",
    suave: "bg-etapa-cinza-suave",
    tinta: "text-etapa-cinza-tinta",
    borda: "border-etapa-cinza",
    nome: "Cinza",
  },

  /* ─── Semânticos, aceitos por compatibilidade ─────────────────────── */
  marca: { faixa: "bg-marca", suave: "bg-marca-suave", tinta: "text-marca-tinta", borda: "border-marca", nome: "Marca" },
  cool: { faixa: "bg-cool", suave: "bg-cool-suave", tinta: "text-cool-tinta", borda: "border-cool", nome: "Azul claro" },
  ember: { faixa: "bg-ember", suave: "bg-ember-suave", tinta: "text-ember-tinta", borda: "border-ember", nome: "Laranja" },
  warn: { faixa: "bg-warn", suave: "bg-warn-suave", tinta: "text-warn-tinta", borda: "border-warn", nome: "Mostarda" },
  ok: { faixa: "bg-ok", suave: "bg-ok-suave", tinta: "text-ok-tinta", borda: "border-ok", nome: "Verde escuro" },
  crit: { faixa: "bg-crit", suave: "bg-crit-suave", tinta: "text-crit-tinta", borda: "border-crit", nome: "Vinho" },
  idle: { faixa: "bg-idle", suave: "bg-idle-suave", tinta: "text-idle-tinta", borda: "border-idle", nome: "Cinza neutro" },
};

/** As que o seletor oferece. Semântico não entra: para etapa nova, a paleta é a de cima. */
export const PALETA_DE_ETAPA = [
  "azul",
  "ciano",
  "violeta",
  "rosa",
  "ambar",
  "verde",
  "vermelho",
  "cinza",
] as const;

const NEUTRA: CorDeEtapa = CORES_DE_ETAPA.cinza as CorDeEtapa;

/** Tom desconhecido cai no neutro em vez de sumir a cor da coluna. */
export function corDaEtapa(tom: string | null | undefined): CorDeEtapa {
  return (tom && CORES_DE_ETAPA[tom]) || NEUTRA;
}

/* ══════════════════════════════════════════════════════════════════════
   O semáforo — ESTADO, portanto paleta semântica
   ══════════════════════════════════════════════════════════════════════ */

export interface Semaforo {
  readonly rotulo: string;
  readonly ponto: string;
  readonly texto: string;
  readonly titulo: string;
}

/**
 * Quanto tempo o cliente está parado na mesma etapa, em português.
 *
 * "12 d" sozinho não é informação: exige que quem lê saiba de cor qual é o
 * limite aceitável. A frase carrega o julgamento, e a cor só reforça — que é
 * a mesma regra de explicabilidade que vale para o teto do número.
 */
export function semaforoDeDias(dias: number): Semaforo {
  if (dias <= 2) {
    return {
      rotulo: "em dia",
      ponto: "bg-ok",
      texto: "text-ok-tinta",
      titulo: `Entrou nesta etapa há ${dias} dia(s). Está no ritmo.`,
    };
  }
  if (dias <= 6) {
    return {
      rotulo: "esfriando",
      ponto: "bg-warn",
      texto: "text-warn-tinta",
      titulo: `${dias} dias parado nesta etapa. Vale um toque antes de virar semana.`,
    };
  }
  return {
    rotulo: `parado há ${dias} d`,
    ponto: "bg-crit",
    texto: "text-crit-tinta",
    titulo: `${dias} dias na mesma etapa, sem andar. É o que a reunião de segunda procura.`,
  };
}

/* ══════════════════════════════════════════════════════════════════════
   Iniciais
   ══════════════════════════════════════════════════════════════════════ */

/** Palavras que não identificam empresa nenhuma e só roubam a inicial. */
const VAZIAS = new Set(["de", "da", "do", "das", "dos", "e", "ltda", "me", "epp", "sa", "s/a"]);

/**
 * Duas letras para o avatar do cartão.
 *
 * O avatar existe para o olho achar o cliente no quadro sem ler — num Kanban
 * de 40 cartões, forma e cor chegam antes do texto. "COMERCIAL SÃO JOÃO LTDA"
 * vira CS, não CO.
 */
export function iniciaisDe(nome: string): string {
  const palavras = nome
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0 && !VAZIAS.has(p.toLowerCase().replace(/[.,]/g, "")));

  const usar = palavras.length > 0 ? palavras : nome.trim().split(/\s+/);
  const letras = usar.slice(0, 2).map((p) => p[0] ?? "");
  const r = letras.join("").toUpperCase();
  return r === "" ? "?" : r;
}
